import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Tabs,
  Tab,
  Card,
  CardContent,
  Avatar,
  Chip,
  Divider
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import SchoolIcon from '@mui/icons-material/School';
import DownloadIcon from '@mui/icons-material/Download';
import MapIcon from '@mui/icons-material/Map';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { generateSchoolsCsv, downloadCsv } from '../../utils/csvExport';

/**
 * Modern organization detail header component 
 */
const OrganizationDetailHeader = ({
  organization,
  onEditClick,
  onNewOrderClick,
  onShowMap,
}) => {
  const isSchool = organization?.isSchool || organization?.is_school;
  
  // Handle export to CSV for school organizations
  const handleExportCsv = () => {
    if (organization && isSchool) {
      const csvContent = generateSchoolsCsv([organization]);
      downloadCsv(csvContent);
    }
  };
  
  return (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center', 
      p: 3, 
      borderBottom: `1px solid ${theme => theme.palette.divider}`
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Avatar 
          sx={{ 
            bgcolor: isSchool ? 'info.main' : 'secondary.main',
            width: 48,
            height: 48
          }}
        >
          {isSchool ? <SchoolIcon /> : <BusinessIcon />}
        </Avatar>
        <Box sx={{ ml: 2 }}>
          <Typography variant="h5" fontWeight="600">
            {organization?.name || 'Organisation Details'}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
            <Chip 
              label={isSchool ? "School" : "Business"} 
              size="small" 
              color={isSchool ? "info" : "secondary"}
              sx={{ 
                height: '22px', 
                fontSize: '0.75rem',
                fontWeight: 500,
                borderRadius: 1
              }} 
            />
            <Box component="span" sx={{ mx: 1, color: 'text.disabled' }}>•</Box>
            <Typography variant="body2" color="text.secondary">
              ID: {organization?.id || '—'}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Tooltip title="Back to map">
          <IconButton onClick={onShowMap} size="small" sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '8px' }}>
            <MapIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {isSchool && (
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExportCsv}
            sx={{ borderRadius: '8px' }}
          >
            Export Data
          </Button>
        )}
        <Button
          variant="outlined"
          startIcon={<EditIcon />}
          onClick={onEditClick}
          sx={{ borderRadius: '8px' }}
        >
          Edit
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={onNewOrderClick}
          sx={{ borderRadius: '8px' }}
        >
          New Order
        </Button>
      </Box>
    </Box>
  );
};

/**
 * Modern organization detail tabs component 
 */
const OrganizationDetailTabs = ({
  tabValue,
  onTabChange,
  organization,
  children
}) => {
  const isSchool = organization?.isSchool || organization?.is_school;
  
  return (
    <>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs 
          value={tabValue} 
          onChange={onTabChange}
          sx={{ 
            px: 3,
            '& .MuiTab-root': {
              minWidth: 'auto',
              px: 2,
              py: 1.5,
              mx: 1,
              fontSize: '0.875rem',
              fontWeight: 500,
              color: 'text.secondary',
              '&.Mui-selected': {
                color: 'primary.main',
                fontWeight: 600
              }
            },
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: '3px 3px 0 0'
            }
          }}
        >
          <Tab label="Contact Details" />
          {isSchool && <Tab label="Advertising Details" />}
          <Tab label="Orders" />
          <Tab label="Notes" />
          <Tab label="History" />
        </Tabs>
      </Box>
      
      <Box sx={{ p: 3 }}>
        {children}
      </Box>
    </>
  );
};

/**
 * Combined organization detail component 
 */
const OrganizationDetail = ({
  organization,
  tabValue,
  onTabChange,
  onEditClick,
  onNewOrderClick,
  onShowMap,
  children,
}) => {
  if (!organization) return null;

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ p: 0, height: '100%', '&:last-child': { pb: 0 } }}>
        <OrganizationDetailHeader 
          organization={organization}
          onEditClick={onEditClick}
          onNewOrderClick={onNewOrderClick}
          onShowMap={onShowMap}
        />
        
        <OrganizationDetailTabs
          tabValue={tabValue}
          onTabChange={onTabChange}
          organization={organization}
        >
          {children}
        </OrganizationDetailTabs>
      </CardContent>
    </Card>
  );
};

export { OrganizationDetailHeader, OrganizationDetailTabs };
export default OrganizationDetail; 